import axios from 'axios';
import authService from '../auth';
import i18n from '../../i18n/i18n';

// Helper function to get error category from error code
const getErrorCategory = category => {
  if (!category) return 'generic';

  return category.toLowerCase();
};

// Helper function to process error response and extract error codes
const processErrorResponse = error => {
  const response = error.response;
  if (!response?.data) {
    return {
      codes: ['SERVER_ERROR'],
      category: 'generic',
      type: 'error',
      parameters: {},
      validationErrors: {},
      message: null,
    };
  }

  const errorData = response.data;

  // Extract error codes - handle both single code and multiple codes
  let errorCodes = [];
  if (errorData.code) {
    errorCodes.push(errorData.code);
  }
  if (errorData.errors && Array.isArray(errorData.errors)) {
    errorCodes.push(...errorData.errors.map(err => err.code || 'UNKNOWN_ERROR'));
  }
  // Handle validation errors format: { "ERROR_CODE": [{ parameters: {} }] }
  if (
    errorData.errors &&
    typeof errorData.errors === 'object' &&
    !Array.isArray(errorData.errors)
  ) {
    errorCodes.push(...Object.keys(errorData.errors));
  }

  // If no codes found, use default
  if (errorCodes.length === 0) {
    errorCodes = ['SERVER_ERROR'];
  }

  // Extract category, type, and parameters
  const category = errorData.category || 'generic';
  const type = errorData.type || 'error';
  const parameters = errorData.parameters || {};

  // Process validation errors
  const validationErrors = {};
  if (
    errorData.errors &&
    typeof errorData.errors === 'object' &&
    !Array.isArray(errorData.errors)
  ) {
    Object.entries(errorData.errors).forEach(([errorCode, errorInstances]) => {
      if (Array.isArray(errorInstances)) {
        validationErrors[errorCode] = errorInstances.map(err => ({
          code: errorCode,
          parameters: err.parameters || {},
        }));
      }
    });
  }

  return {
    codes: errorCodes,
    category,
    type,
    parameters,
    validationErrors,
    message: errorData.message || null, // Fallback message for development
    // Keep single code for backward compatibility
    code: errorCodes[0],
  };
};

// Helper function to translate error code to user-friendly message
const translateErrorCode = (category, errorCode, parameters = {}) => {
  if (!errorCode) return i18n.t('errors.generic.SERVER_ERROR');

  const errorCategory = getErrorCategory(category);
  let translationKey = `errors.${errorCategory}.${errorCode}`;

  // Try to get translation
  let translation = i18n.t(translationKey, parameters);

  // If translation key wasn't found, try validation category for validation errors
  if (translation === translationKey && category === 'APP_VALIDATION') {
    translationKey = `errors.validation.${errorCode}`;
    translation = i18n.t(translationKey, parameters);
  }

  // If translation key still wasn't found (returns the key itself), use generic error
  if (translation === translationKey) {
    console.warn(`Missing translation for error code: ${errorCode} (key: ${translationKey})`);
    return i18n.t('errors.generic.SERVER_ERROR');
  }

  return translation;
};

const translateErrorCodes = (category, errorCodes, parameters = {}) => {
  return errorCodes.map(errorCode => translateErrorCode(category, errorCode, parameters));
};

// Helper function to generate user-friendly error messages from processed error
const generateUserFriendlyMessages = processedError => {
  if (processedError.codes.length === 1) {
    return [
      translateErrorCode(
        processedError.category,
        processedError.codes[0],
        processedError.parameters
      ),
    ];
  } else {
    return translateErrorCodes(
      processedError.category,
      processedError.codes,
      processedError.parameters
    );
  }
};

// Get API URL from environment variables
const API_BASE_URL = window._env_.REACT_APP_API_BASE_URL;

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with requests
});

// Flag to prevent infinite logout loops
let isLoggingOut = false;

// Request interceptor for authentication
apiClient.interceptors.request.use(
  async config => {
    // No need to add token to headers as it's handled by HttpOnly cookies
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling auth errors
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const { response } = error;

    // Process error response to extract error codes and structure
    const processedError = processErrorResponse(error);

    // Attach processed error information to the error object
    error.errorInfo = processedError;

    // If error is 401 (Unauthorized), logout immediately
    if (response?.status === 401) {
      // Authentication error: Token invalid or expired

      // Skip auto-logout for the token exchange endpoint — the user was never
      // authenticated in the backoffice, so there is no session to clear. The
      // calling component (AuthCallback) handles these errors specifically.
      const requestUrl = error.config?.url || '';
      const isTokenExchange = requestUrl.includes('/api/auth/token');

      if (!isTokenExchange) {
        // Prevent infinite loop if logout itself returns 401
        if (!isLoggingOut) {
          isLoggingOut = true;

          try {
            // Logout and redirect to login (refresh token functionality disabled)
            await authService.logout();
          } finally {
            // Reset the flag after logout completes (success or failure)
            isLoggingOut = false;
          }
        } else {
          // Already logging out, skipping additional logout call

          // If we're already in the logout process and got another 401,
          // just ensure we redirect to login
          authService.redirectToLogin();
        }
      }

      // Use error code-based translation for auth errors
      error.userFriendlyMessages = generateUserFriendlyMessages(processedError);

      // Return the error to be handled by the component
      return Promise.reject(error);
    }

    // Handle 422 Unprocessable Entity for BusinessRuleViolationException
    if (response?.status === 422) {
      // Use error code-based translation
      error.userFriendlyMessages = generateUserFriendlyMessages(processedError);
      return Promise.reject(error);
    }

    // Handle 403 Forbidden errors
    if (response?.status === 403) {
      // Use error code-based translation
      error.userFriendlyMessages = generateUserFriendlyMessages(processedError);
      return Promise.reject(error);
    }

    // Handle 404 Not Found errors
    if (response?.status === 404) {
      // Use error code-based translation
      error.userFriendlyMessages = generateUserFriendlyMessages(processedError);
      return Promise.reject(error);
    }

    // Handle 400 Bad Request errors (including validation errors)
    if (response?.status === 400) {
      // For validation errors, we'll handle them specially in the Error Context
      // For now, use the main error code translation
      error.userFriendlyMessages = generateUserFriendlyMessages(processedError);
      return Promise.reject(error);
    }

    // Handle 500 and other server errors
    if (response?.status >= 500) {
      // Use error code-based translation
      error.userFriendlyMessages = generateUserFriendlyMessages(processedError);
      return Promise.reject(error);
    }

    // Handle 503 Service Unavailable errors
    if (response?.status === 503) {
      // Use error code-based translation
      error.userFriendlyMessages = [
        translateErrorCode(
          processedError.category,
          'SERVICE_UNAVAILABLE',
          processedError.parameters
        ),
      ];
      return Promise.reject(error);
    }

    // Handle other errors or network issues
    if (!response) {
      error.userFriendlyMessages = [
        i18n.t(
          'errors.generic.SERVER_ERROR',
          'Unable to connect to the server. Please check your internet connection.'
        ),
      ];
      return Promise.reject(error);
    }

    // For any other unhandled error types
    error.userFriendlyMessages = generateUserFriendlyMessages(processedError);

    return Promise.reject(error);
  }
);

export default apiClient;
